import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Modal,
  Backdrop,
  Fade,
  TextField,
  InputLabel,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core';
import Text from '../../Typography/Text.js';
import Button from '../../Buttons/Button.js';
import CloseButton from '../../Buttons/CloseButton.js';
import fontFamily from '../../../assets/css/fonts.js';
import color from '../../../assets/css/colors.js';
import {
  currentUserDetails,
  updateUserSettings,
} from '../../../services/AdministratorService.js';
import { notifySuccess, notifyError } from '../../Messages/Notification.js';
import TextFieldMobileNoAreaCode from '../../Textfields/TextFieldMobileNoAreaCode.js';

import ChangePasswordModal from '../ChangePasswordModal.js';
import { CheckboxAuthenticatorApp } from './CheckboxAuthenticatorApp.js';

const useStyles = makeStyles(() => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: 550,
    width: '100%',
    margin: '0 auto',
    float: 'clear',
  },
  modalHeader: {
    backgroundColor: 'white',
    padding: 25,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    display: 'flex',
    '& div:nth-child(1)': {
      flex: 1,
    },
    '& div:nth-child(2)': {
      flex: 'none',
      '& .MuiIconButton-root': {
        borderRadius: 5,
        padding: 10,
        '&:hover svg path': {
          fill: color.primary.main,
        },
      },
      '& svg:last-child': {
        width: 15,
        height: 15,
      },
    },
  },
  modalBody: {
    backgroundColor: 'white',
    padding: '5px 25px 25px 25px',
    fontFamily: fontFamily.headers,
    '& p': {
      fontSize: 16,
      lineHeight: '24px',
    },
  },
  modalFooter: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    backgroundColor: 'white',
    padding: '0px 25px 25px 25px',
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
    position: 'relative',
    '& button': {
      width: 'auto',
    },
  },
  closeButton: {
    '& button': {
      marginTop: '-4px !important',
    },
  },
}));

export default function UserSettingsModal(props) {
  const classes = useStyles();

  const { open, close } = props;
  const [openChangePassword, setOpenChangePassword] = React.useState(false);

  const [loading, setLoading] = React.useState({
    save: false,
    auth: false,
  });

  const [modalData, setModalData] = React.useState({
    usrId: '',
    roleId: 0,
    roleName: '',
    name: '',
    email: '',
    mobileNo: '',
    authenticationText: false,
    authenticationEmail: false,
    authenticationAuthenticator: false,
    authenticationMode: 'Email',
    hasMfaKeys: false,
    correspondent: '',
  });

  const handleCloseChangePassword = () => {
    setOpenChangePassword(false);
  };

  const handleChangePassword = () => {
    setOpenChangePassword(true);
  };

  React.useEffect(() => {
    async function init() {
      const user = await currentUserDetails();

      setModalData({
        usrId: user.usrId,
        name: user.name,
        email: user.email,
        mobileNo: user.mobileNo,
        roleName: user.roleName,
        correspondent: user.correspondent,
        authenticationText: user.authenticationMode?.includes('Text'),
        authenticationEmail: user.authenticationMode?.includes('Email'),
        authenticationAuthenticator: user.authenticationMode?.includes('Authenticator'),
        hasMfaKeys: user.hasMfaKeys,
      });
    }
    if (open) {
      init();
    }
  }, [open]);

  const handleContactInfoChange = (value, data, event, formattedValue) => {
    setModalData({
      ...modalData,
      mobileNo: formattedValue,
    });
  };

  const handleChange = async (e, checkboxValue) => {
    const input = e.currentTarget?.name ? e.currentTarget : e.target;

    setModalData((current) => {
      return {
        ...current,
        [input.name]:
          checkboxValue === true || checkboxValue === false ? checkboxValue : input.value,
      };
    });
  };

  const handleSave = async (data) => {
    let valid = true;

    if (!data.mobileNo) {
      notifyError('Mobile No is required.');
      valid = false;
    }

    if (
      !data.authenticationEmail &&
      !data.authenticationText &&
      !data.authenticationAuthenticator
    ) {
      notifyError('At least 1 authentication mode is required.');
      valid = false;
    } else {
      var mode = '';
      mode = data.authenticationEmail ? 'Email,' : '';
      mode = mode + (data.authenticationText ? 'Text,' : '');
      mode = mode + (data.authenticationAuthenticator ? 'Authenticator,' : '');
      data.authenticationMode = mode;
    }

    if (!valid) {
      return;
    }

    try {
      setLoading({ ...loading, save: true });
      const res = await updateUserSettings(data);
      notifySuccess(res.success);
      setLoading({ ...loading, save: false });
      handleClose();
    } catch (error) {
      notifyError(error);
      setLoading({ ...loading, save: false });
      return;
    }
  };

  const handleClose = () => {
    close();
  };

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      className={classes.modal}
      open={open}
      onClose={close}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={open}>
        <div style={{ width: 1000 }}>
          <div className={classes.modalHeader}>
            <div>
              <Text mt={4} variant="h2" label="User Settings" />
            </div>
            <div className={classes.closeButton}>
              <CloseButton onClick={handleClose} />
            </div>
          </div>
          <div className={classes.modalBody}>
            <form autoComplete="off">
              <></>
              <div className="grd-row">
                <div className="grd-cell" style={{ marginRight: 30 }}>
                  <TextField
                    fullWidth
                    disabled
                    name="name"
                    label="Name"
                    value={modalData.name}
                    InputLabelProps={{ shrink: true }}
                  />
                </div>
              </div>
              <div className="grd-row">
                <div className="grd-cell" style={{ marginRight: 30 }}>
                  <TextField
                    fullWidth
                    disabled
                    name="email"
                    label="Email"
                    value={modalData.email}
                    InputLabelProps={{ shrink: true }}
                  />
                </div>
              </div>
              <div className="grd-row">
                <div className="grd-cell" style={{ marginRight: 30 }}>
                  <TextField
                    fullWidth
                    disabled
                    name="correspondent"
                    label="Correspondent"
                    value={modalData.correspondent}
                    InputLabelProps={{ shrink: true }}
                  />
                </div>
                <div className="grd-cell" style={{ marginRight: 30 }}>
                  <TextField
                    fullWidth
                    disabled
                    name="roleName"
                    label="Role Name"
                    value={modalData.roleName}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </div>
              </div>
              <div className="grd-row">
                <div className="grd-cell" style={{ marginRight: 30 }}>
                  <TextFieldMobileNoAreaCode
                    required={true}
                    name="mobileNo"
                    label="Mobile No"
                    value={modalData.mobileNo}
                    onChange={handleContactInfoChange}
                  ></TextFieldMobileNoAreaCode>
                </div>
              </div>
              <div className="grd-row">
                <div className="grd-cell" style={{ marginRight: 30 }}>
                  <div>
                    <InputLabel>Send code authentication via:</InputLabel>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="authenticationText"
                          checked={modalData.authenticationText}
                          onChange={handleChange}
                        />
                      }
                      label="Text"
                    />
                  </div>
                  <div>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="authenticationEmail"
                          checked={modalData.authenticationEmail}
                          onChange={handleChange}
                        />
                      }
                      label="Email"
                    />
                  </div>
                  <div>
                    <CheckboxAuthenticatorApp
                      disabled={!modalData.hasMfaKeys}
                      name="authenticationAuthenticator"
                      checked={modalData.authenticationAuthenticator}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
              <div className={classes.modalFooter}>
                <div className="grd-cell-none" style={{ marginRight: 10, marginTop: 20 }}>
                  <Button
                    color="primary"
                    label="Change Password"
                    onClick={handleChangePassword}
                  />
                </div>
                <div className="grd-cell-none">
                  <Button
                    disabled={loading.save}
                    loading={loading.save}
                    color="secondary"
                    label={loading.save ? 'Saving...' : 'Save'}
                    onClick={() => {
                      handleSave(modalData);
                    }}
                  />
                </div>
              </div>
            </form>
            {openChangePassword && (
              <ChangePasswordModal
                open={openChangePassword}
                close={handleCloseChangePassword}
              />
            )}
          </div>
        </div>
      </Fade>
    </Modal>
  );
}
